// Reusable banner that nudges unverified users to verify their email.
// Mount it inside any authed page that wants to surface the prompt
// (HomePage + SettingsPage in the boilerplate).
//
// The API auto-sends one verification email on registration. This banner
// exists so users can request a *resend* if the first email never arrived
// or they're on a fresh session waiting for a new link.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sdk } from '../lib/sdk';

interface Props {
  email: string;
  /** Hide the banner once the user has verified — caller toggles `isEmailVerified`. */
  visible: boolean;
}

export default function VerifyEmailBanner({ email, visible }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'failed'>('idle');

  if (!visible) return null;

  async function onResend() {
    setBusy(true);
    setStatus('idle');
    try {
      await sdk.auth.sendVerificationEmail();
      setStatus('sent');
    } catch {
      setStatus('failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="status" style={banner}>
      <span style={{ flex: 1 }}>{t('verifyEmail.banner', { email })}</span>
      {status === 'sent' ? (
        <span style={{ color: '#066' }}>{t('verifyEmail.sent')}</span>
      ) : (
        <button onClick={onResend} disabled={busy} style={btn}>
          {busy ? '…' : t('verifyEmail.resend')}
        </button>
      )}
      {status === 'failed' && <span style={{ color: 'red', marginLeft: '0.5rem' }}>{t('verifyEmail.failed')}</span>}
    </div>
  );
}

const banner: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.75rem 1rem',
  background: '#fff8d6',
  border: '1px solid #f0d040',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.9rem',
};
const btn: React.CSSProperties = {
  padding: '0.35rem 0.75rem',
  cursor: 'pointer',
  background: '#fff',
  border: '1px solid #d0c060',
  borderRadius: 4,
  font: 'inherit',
};
